import { isAxiosError } from 'axios';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { novaOpsApi } from '@/lib/novaOpsApi';

type Eligibility = {
  eligible: boolean;
  canResend: boolean;
  reason: string;
  retryAfterSec?: number;
};

export function RicaDeadlineRecovery({
  accountUuid,
  mvno,
  showUnavailableReason = false,
}: {
  accountUuid: string;
  mvno: string;
  showUnavailableReason?: boolean;
}) {
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [deliveryKey, setDeliveryKey] = useState(() => crypto.randomUUID());
  const query = useQuery({
    queryKey: ['rica-deadline', mvno, accountUuid],
    queryFn: async () =>
      (
        await novaOpsApi.get<Eligibility>('/ops/customers/rica-deadline', {
          params: { mvno, account_uuid: accountUuid },
        })
      ).data,
    refetchInterval: 60_000,
    retry: false,
  });
  if (!query.data?.eligible) {
    if (!showUnavailableReason) return null;
    return (
      <p className="mt-2 text-sm" role="status">
        {query.isPending
          ? 'Checking whether the verification link can be resent…'
          : query.isError
            ? 'Unable to check resend availability. Please refresh or contact support.'
            : 'Resend is unavailable for this suspension. The verification case needs review.'}
      </p>
    );
  }

  async function resend() {
    setSending(true);
    setMessage('');
    try {
      await novaOpsApi.post('/ops/customers/rica-deadline/resend-link', {
        mvno,
        account_uuid: accountUuid,
        delivery_key: deliveryKey,
      });
      setMessage(
        'The existing RICA link was sent by SMS. Service will resume automatically after verification is approved.',
      );
      setDeliveryKey(crypto.randomUUID());
    } catch (error) {
      // Preserve the key on a lost response so retry cannot send twice.
      if (isAxiosError(error) && error.response)
        setDeliveryKey(crypto.randomUUID());
      setMessage(
        'The resend could not be confirmed. Refresh the verification status before retrying.',
      );
    } finally {
      setSending(false);
      void query.refetch();
    }
  }

  return (
    <div className="mt-3 space-y-2">
      {query.data.canResend && (
        <Button
          type="button"
          variant="outline"
          disabled={sending}
          onClick={() => void resend()}
        >
          {sending ? 'Sending…' : 'Resend RICA verification link'}
        </Button>
      )}
      <p className="text-sm" role="status">
        {message ||
          (query.data.reason === 'RECOVERY_PENDING'
            ? 'Verification passed. Automatic service recovery is pending.'
            : query.data.reason === 'PROVIDER_UNAVAILABLE'
              ? 'Verification status is temporarily unavailable. Please try again shortly.'
              : query.data.reason === 'IN_PROGRESS'
                ? 'Verification is under review. Service will resume automatically after approval.'
                : query.data.retryAfterSec
                  ? 'Please wait one minute before resending.'
                  : query.data.canResend
                    ? 'Resend the existing verification link. The original deadline stays in place.'
                    : 'This verification needs case review before a link can be resent.')}
      </p>
    </div>
  );
}
